import { useState, useEffect } from "react";
import { connect } from "react-redux";

import {
  Input,
  Row,
  Typography,
  Card,
  Select,
  Form,
  Spin,
  Col,
  Divider,
  InputNumber,
  Button,
  Space,
  Image,
  Table,
} from "antd";
import {
  DeleteOutlined,
  SearchOutlined,
  ArrowRightOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import axios from "axios";
import Cookies from "js-cookie";
import appConfig from "../../../config/appConfig";
import { useNavigate } from "react-router-dom";

import "../../../assets/styles/DatePicker.css";
import MainLayout from "../../../components/layouts/MainLayout";
import CustomButton from "../../../components/custom/CustomButton";
import TopContent from "../../../components/layouts/TopContent";
import cnt from "../../../services/definedConstant";
import { BOM } from "../../../services/definedMethod";
import { apiConfig, labelConfig, staticConfig } from "../teller.config";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

const { Text } = Typography;
const { TextArea } = Input;

// Static denomination options (used as fallback when vault template is unavailable)
const STATIC_DENOM_OPTIONS = [
  { value: 5, label: "5", denomValue: 5 },
  { value: 10, label: "10", denomValue: 10 },
  { value: 20, label: "20", denomValue: 20 },
  { value: 50, label: "50", denomValue: 50 },
  { value: 100, label: "100", denomValue: 100 },
  { value: 200, label: "200", denomValue: 200 },
  { value: 500, label: "500", denomValue: 500 },
  { value: 1000, label: "1,000", denomValue: 1000 },
  { value: 2000, label: "2,000", denomValue: 2000 },
  { value: 5000, label: "5,000", denomValue: 5000 },
];

// ── Info row — same style as GLDetailPanel ────────────────────────────────────
const InfoRow = ({ label, value, last }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      borderBottom: last ? 0 : "1px solid #F3F4F6",
      minHeight: 40,
    }}
    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#F9FAFB")}
    onMouseLeave={(e) =>
      (e.currentTarget.style.backgroundColor = "transparent")
    }
  >
    <div
      style={{
        flex: "0 0 45%",
        padding: "8px 12px",
        backgroundColor: "#F8FAFC",
        borderRight: "1px solid #E5E7EB",
        fontWeight: 600,
        fontSize: 12,
        color: "#374151",
        minHeight: 40,
        display: "flex",
        alignItems: "center",
      }}
    >
      {label}
    </div>
    <div
      style={{
        flex: "0 0 28px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <ArrowRightOutlined style={{ fontSize: 11, color: "#9ca3af" }} />
    </div>
    <div
      style={{
        flex: 1,
        padding: "8px 10px 8px 0",
        fontSize: 12,
        color: "#111827",
        display: "flex",
        alignItems: "center",
      }}
    >
      {value ?? "—"}
    </div>
  </div>
);

const NewCashDeposit = (props) => {
  const { userData } = props;
  const { session } = userData;

  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [paymentTypeData, setPaymentTypeData] = useState([]);
  const [savingsAccountDetails, setSavingsAccountDetails] = useState(null);
  const [clientSignature, setClientSignature] = useState(null);
  const [isPhotoPreviewVisible, setIsPhotoPreviewVisible] = useState(false);
  const [denomOptions, setDenomOptions] = useState([]);
  const [denomInput, setDenomInput] = useState({
    denomCode: null,
    denomCount: 1,
    denomValue: 0,
  });
  const [denomEntries, setDenomEntries] = useState([]);
  const [accountSearchValue, setAccountSearchValue] = useState("");

  useEffect(() => {
    const intervalId = setInterval(() => setCurrentDate(new Date()), 1000);
    return () => clearInterval(intervalId);
  }, []);

  function getAccountDetails(accountNo) {
    if (!accountNo) return;
    setIsLoading(true);
    const url = `${apiConfig.GetByAccountNo}/${accountNo}?associations=all`;

    BOM.FetchReqAction({ session }, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err?.errors?.[0]?.defaultUserMessage || err);
        setIsLoading(false);
      } else {
        setSavingsAccountDetails(res);
        getTemplate(res);
        getClientDetails(res.clientId, res.officeId);
      }
    });
  }

  function getTemplate(accParam) {
    const url = `${apiConfig.savingsAccounts}/${accParam.id}/transactions/template`;

    BOM.FetchReqAction({ session }, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err?.errors?.[0]?.defaultUserMessage || err);
        setIsLoading(false);
      } else {
        const tempPaymentType = (res?.paymentTypeOptions || []).map((el) => ({
          value: el.id,
          label: el.name,
        }));
        setPaymentTypeData(tempPaymentType);
        setIsLoading(false);
      }
    });
  }


  async function fetchClientSignature(cId) {
    try {
      const docs = await new Promise((resolve, reject) => {
        BOM.FetchReqAction(
          { session },
          `${apiConfig.Clients}/${cId}/documents`,
          (err, res) => (err ? reject(err) : resolve(res || [])),
        );
      });
      const unique = BOM.GetUnique(docs, "name");
      const sig = unique.find(
        (d) => d.name?.toLowerCase() === "clientsignature",
      );
      if (!sig) return;

      const url = `${apiConfig.Clients}/${cId}/documents/${sig.id}/attachment?tenantIdentifier=default`;
      const { headers } = appConfig.apiSettings;
      headers.Authorization = session;
      const tfaToken = Cookies.get("tfaToken");
      if (tfaToken && tfaToken !== "null" && tfaToken !== "undefined") {
        headers["Fineract-Platform-TFA-Token"] = tfaToken;
      } else {
        delete headers["Fineract-Platform-TFA-Token"];
      }
      const response = await axios({ method: "GET", url, headers, responseType: "blob" });
      setClientSignature(URL.createObjectURL(new Blob([response.data])));
    } catch (e) {
      console.error("Error fetching client signature:", e);
    }
  }

  function getClientDetails(cId, officeId) {
    if (!cId) return;

    fetchClientSignature(cId);

    if (officeId) {
      getVaultTemplate(officeId);
    }
  }

  function getVaultTemplate(officeId) {
    const url = `${apiConfig.VaultBatch}/template?officeId=${officeId}`;

    BOM.FetchReqAction({ session }, url, (err, res) => {
      if (!err && res?.denominations) {
        const options = res.denominations.map((d) => ({
          value: d.currency || d.code || d.id,
          label: d.description || d.currency || d.code,
          denomValue: d.amount || d.value || 0,
        }));
        setDenomOptions(options);
      }
    });
  }

  const onSearchAccount = () => {
    getAccountDetails(accountSearchValue);
  };

  const onCancel = () => navigate("/tlo0800", { replace: false });

  const onSubmit = (formValues) => {
    if (!savingsAccountDetails?.id) {
      BOM.AlertMsg(cnt.DANGER, "Please look up a valid account first.");
      return;
    }

    const denominations = denomEntries
      .filter((r) => r.denomCode)
      .map((r) => ({
        currency: r.denomCode,
        amount: r.denomValue,
        count: r.denomCount,
        locale: "en",
      }));

    const url = `${apiConfig.savingsAccounts}/${savingsAccountDetails.id}/transactions`;
    const body = {
      transactionDate: BOM.DateTime(formValues.transactionDate, "only-date"),
      transactionAmount: formValues.transactionAmount,
      paymentTypeId: formValues.paymentTypeId,
      note: formValues.note,
      ...(denominations.length > 0 && { denominations }),
      dateFormat: "dd MMMM yyyy",
      locale: "en",
      method: "POST",
      session,
      params: { command: "deposit" },
    };

    setIsLoading(true);
    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err?.errors?.[0]?.defaultUserMessage || err);
        setIsLoading(false);
      } else {
        BOM.AlertMsg(cnt.SUCCESS, "Deposit action was successful");
        navigate("/tlo0800");
        setIsLoading(false);
      }
    });
  };

  // Denomination helpers
  const handleDenomInputChange = (field, value) => {
    setDenomInput((prev) => {
      const activeDenomOptions = denomOptions.length
        ? denomOptions
        : STATIC_DENOM_OPTIONS;
      if (field === "denomCode") {
        const option = activeDenomOptions.find((o) => o.value === value);
        const unitValue = option?.denomValue ?? 0;
        return {
          ...prev,
          denomCode: value,
          denomValue: unitValue * prev.denomCount,
        };
      }
      if (field === "denomCount") {
        const option = activeDenomOptions.find(
          (o) => o.value === prev.denomCode,
        );
        const unitValue = option?.denomValue ?? 0;
        return {
          ...prev,
          denomCount: value,
          denomValue: unitValue * (value || 0),
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleAddDenom = () => {
    const { denomCode, denomCount, denomValue } = denomInput;
    if (!denomCode || !denomCount) return;
    const activeDenomOptions = denomOptions.length
      ? denomOptions
      : STATIC_DENOM_OPTIONS;
    const option = activeDenomOptions.find((o) => o.value === denomCode);
    setDenomEntries((prev) => [
      ...prev,
      {
        id: Date.now(),
        denomCode,
        denomLabel: option?.label ?? String(denomCode),
        denomCount,
        denomValue,
      },
    ]);
    setDenomInput({ denomCode: null, denomCount: 1, denomValue: 0 });
  };

  const removeDenomEntry = (id) => {
    setDenomEntries((prev) => prev.filter((e) => e.id !== id));
  };

  dayjs.extend(customParseFormat);
  const dateFormat = "YYYY-MM-DD";
  const formattedDate = currentDate.toISOString().split("T")[0];
  const date = dayjs(formattedDate, dateFormat);
  const today = BOM.DateTime(date, "only-date");

  const accountSummaryRows = savingsAccountDetails
    ? [
        { label: "Account Name", value: savingsAccountDetails.clientName },
        {
          label: "Currency",
          value: savingsAccountDetails?.currency?.displayLabel,
        },
        {
          label: "Current Balance",
          value: Number(
            savingsAccountDetails?.summary?.accountBalance ??
            savingsAccountDetails?.accountBalance
          ).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        },
        {
          label: "Available Balance",
          value: Number(
            savingsAccountDetails?.summary?.availableBalance ??
            savingsAccountDetails?.availableBalance
          ).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        },
      ]
    : [];

  return (
    <MainLayout isEnableRightSidebar={false} onSave={onSubmit}>
      <TopContent
        pageTitle={labelConfig.newCashDeposit}
        pageItems={staticConfig.newCashDepositItems}
      />
      <Spin spinning={isLoading} size="large">
        <Card
          style={{
            boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
            borderRadius: 20,
          }}
        >
          <Form
            initialValues={{ transactionDate: today }}
            form={form}
            layout="vertical"
            onFinish={onSubmit}
          >
            {/* ── Account Lookup ─────────────────────────────────────────── */}
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#9ca3af",
                letterSpacing: 1,
                textTransform: "uppercase",
                marginBottom: 10,
              }}
            >
              Account Lookup
            </div>

            <Row style={{ marginBottom: 24 }}>
              <Col span={12}>
                <Form.Item
                  label={labelConfig.accountNo}
                  name="accountNo"
                  style={{ marginBottom: 0 }}
                  rules={[
                    {
                      required: true,
                      message: BOM.CheckReqFld(labelConfig.accountNo),
                    },
                  ]}
                >
                  <Space.Compact style={{ width: "100%" }}>
                    <Input
                      placeholder={BOM.handlePlaceholderMsg(
                        labelConfig.accountNo,
                      )}
                      style={{ height: 44 }}
                      value={accountSearchValue}
                      onChange={(e) => setAccountSearchValue(e.target.value)}
                      onPressEnter={onSearchAccount}
                      allowClear
                    />
                    <Button
                      className="of-primaryBtnColor"
                      onClick={onSearchAccount}
                      style={{ height: 44 }}
                    >
                      <SearchOutlined />
                    </Button>
                  </Space.Compact>
                </Form.Item>
              </Col>
            </Row>

            {/* ── Account Summary + Client Signature (side by side) ──────── */}
            {savingsAccountDetails && (
              <Row gutter={24} style={{ marginBottom: 24 }}>
                {/* Account Summary */}
                <Col span={14}>
                  <div
                    style={{
                      border: "1px solid #E5E7EB",
                      borderRadius: 10,
                      overflow: "hidden",
                      height: 210,
                    }}
                  >
                    <div
                      style={{
                        padding: "8px 12px",
                        background:
                          "linear-gradient(135deg, rgba(21,204,174,0.1) 0%, rgba(79,236,160,0.1) 100%)",
                        borderBottom: "1px solid #E5E7EB",
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#0d9488",
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                      }}
                    >
                      Account Summary
                    </div>
                    {accountSummaryRows.map((row, i) => (
                      <InfoRow
                        key={row.label}
                        label={row.label}
                        value={row.value}
                        last={i === accountSummaryRows.length - 1}
                      />
                    ))}
                  </div>
                </Col>

                {/* Client Signature */}
                <Col span={10}>
                  <div
                    style={{
                      border: "1px solid rgba(21,204,174,0.25)",
                      borderRadius: 10,
                      overflow: "hidden",
                      height: 210,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      style={{
                        padding: "8px 12px",
                        background:
                          "linear-gradient(135deg, rgba(21,204,174,0.1) 0%, rgba(79,236,160,0.1) 100%)",
                        borderBottom: "1px solid rgba(21,204,174,0.2)",
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#0d9488",
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexShrink: 0,
                      }}
                    >
                      <span>Client Signature</span>
                      {clientSignature && (
                        <Button
                          size="small"
                          icon={<EyeOutlined />}
                          type="text"
                          style={{ color: "#15CCAE", fontSize: 11 }}
                          onClick={() => setIsPhotoPreviewVisible(true)}
                        >
                          View Full
                        </Button>
                      )}
                    </div>
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "12px 16px",
                        background: "#fafafa",
                        overflow: "hidden",
                      }}
                    >
                      <Image
                        style={{ display: "none" }}
                        src={clientSignature || ""}
                        preview={{
                          visible: isPhotoPreviewVisible,
                          onVisibleChange: (v) => setIsPhotoPreviewVisible(v),
                          src: clientSignature,
                        }}
                      />
                      {clientSignature ? (
                        <img
                          src={clientSignature}
                          alt="Client Signature"
                          onClick={() => setIsPhotoPreviewVisible(true)}
                          style={{
                            maxWidth: "100%",
                            maxHeight: 110,
                            objectFit: "contain",
                            borderRadius: 6,
                            border: "1px solid #e5e7eb",
                            cursor: "zoom-in",
                          }}
                        />
                      ) : (
                        <Text style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic" }}>
                          No signature on file
                        </Text>
                      )}
                      <Text
                        style={{
                          marginTop: 10,
                          color: "#374151",
                          fontSize: 13,
                          fontWeight: 600,
                          textAlign: "center",
                        }}
                      >
                        {savingsAccountDetails?.clientName || "—"}
                      </Text>
                      {savingsAccountDetails?.accountNo && (
                        <Text
                          style={{
                            fontSize: 11,
                            color: "#9ca3af",
                            fontFamily: "monospace",
                          }}
                        >
                          {savingsAccountDetails.accountNo}
                        </Text>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            {/* ── Transaction Details ───────────────────────────────────────── */}
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#9ca3af",
                letterSpacing: 1,
                textTransform: "uppercase",
                marginBottom: 14,
              }}
            >
              {labelConfig.depositSetup}
            </div>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label={labelConfig.transactionDate}
                  name="transactionDate"
                  rules={[
                    {
                      required: true,
                      message: BOM.CheckReqFld(labelConfig.transactionDate),
                    },
                  ]}
                >
                  <Input style={{ height: 44 }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label={labelConfig.amountTCY}
                  name="transactionAmount"
                  rules={[
                    {
                      required: true,
                      message: BOM.CheckReqFld(labelConfig.amountTCY),
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%", height: 44 }}
                    formatter={(v) =>
                      `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(v) => v?.replace(/,/g, "")}
                    placeholder="50,000"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label={labelConfig.paymentType}
                  name="paymentTypeId"
                  rules={[
                    {
                      required: true,
                      message: BOM.CheckReqFld(labelConfig.paymentType),
                    },
                  ]}
                >
                  <Select
                    placeholder={BOM.handlePlaceholderMsg(
                      labelConfig.paymentType,
                      "select",
                    )}
                    showSearch
                    options={paymentTypeData}
                    style={{ height: 44 }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={labelConfig.accountCurrency}>
                  <Input
                    disabled
                    className="of-bold-disable-input"
                    style={{ height: 44 }}
                    value={savingsAccountDetails?.currency?.displayLabel || ""}
                    placeholder={BOM.handlePlaceholderMsg(
                      labelConfig.accountCurrency,
                    )}
                  />
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item label={labelConfig.note} name="note">
                  <TextArea
                    rows={2}
                    placeholder={BOM.handlePlaceholderMsg(labelConfig.note)}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* ── Denomination ──────────────────────────────────────────────── */}
            <Divider style={{ marginTop: 8, marginBottom: 16 }} />
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#9ca3af",
                letterSpacing: 1,
                textTransform: "uppercase",
                marginBottom: 10,
              }}
            >
              Denomination
            </div>

            <Row gutter={16} align="bottom" style={{ marginBottom: 12 }}>
              <Col span={6}>
                <div style={{ marginBottom: 4 }}>
                  <Text
                    style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}
                  >
                    Denom Code
                  </Text>
                </div>
                <Select
                  style={{ width: "100%", height: 44 }}
                  placeholder="Select denomination"
                  value={denomInput.denomCode}
                  options={
                    denomOptions.length ? denomOptions : STATIC_DENOM_OPTIONS
                  }
                  onChange={(v) => handleDenomInputChange("denomCode", v)}
                  showSearch
                  allowClear
                />
              </Col>
              <Col span={6}>
                <div style={{ marginBottom: 4 }}>
                  <Text
                    style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}
                  >
                    Denom Count
                  </Text>
                </div>
                <InputNumber
                  style={{ width: "100%", height: 44 }}
                  min={1}
                  value={denomInput.denomCount}
                  onChange={(v) => handleDenomInputChange("denomCount", v)}
                />
              </Col>
              <Col span={6}>
                <div style={{ marginBottom: 4 }}>
                  <Text
                    style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}
                  >
                    Denom Value
                  </Text>
                </div>
                <InputNumber
                  style={{ width: "100%", height: 44 }}
                  className="of-bold-disable-input"
                  min={0}
                  value={denomInput.denomValue}
                  formatter={(v) =>
                    `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(v) => v?.replace(/,/g, "")}
                  disabled
                />
              </Col>
              <Col span={6}>
                <Button
                  type="primary"
                  className="of-primaryBtnColor"
                  style={{ height: 44 }}
                  onClick={handleAddDenom}
                >
                  Add
                </Button>
              </Col>
            </Row>

            {denomEntries.length > 0 && (
              <Table
                style={{ marginTop: 8 }}
                dataSource={denomEntries}
                rowKey="id"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: "Denom Code",
                    dataIndex: "denomLabel",
                    key: "denomLabel",
                  },
                  {
                    title: "Denom Count",
                    dataIndex: "denomCount",
                    key: "denomCount",
                  },
                  {
                    title: "Denom Value",
                    dataIndex: "denomValue",
                    key: "denomValue",
                    render: (v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ","),
                  },
                  {
                    title: "Action",
                    key: "action",
                    render: (_, record) => (
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        className="of-primaryBtnColor"
                        onClick={() => removeDenomEntry(record.id)}
                      />
                    ),
                  },
                ]}
              />
            )}

            {/* ── Actions ───────────────────────────────────────────────────── */}
            <Row style={{ marginTop: 24 }}>
              <Divider />
              <CustomButton
                content="Submit"
                style={{
                  width: "10%",
                  marginLeft: "auto",
                  boxShadow: "0px 0px 10px 5px rgba(0,0,0,0.1)",
                }}
                htmlType="submit"
              />
              <CustomButton
                content="Cancel"
                style={{
                  width: "10%",
                  marginLeft: "1%",
                  boxShadow: "0px 0px 10px 5px rgba(0,0,0,0.1)",
                }}
                backgroundColor="#fff"
                colorText="black"
                hoverBackground="#F5F5F5"
                onClick={onCancel}
              />
            </Row>
          </Form>
        </Card>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps, null)(NewCashDeposit);
